import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { isAdminRole } from "@/lib/accountRole";
import { useInboxUnread } from "@/context/InboxUnreadContext";
import Navbar from "@/components/Navbar";
import MarketplaceThread from "@/components/inbox/MarketplaceThread";
import StaffChatPane from "@/components/inbox/StaffChatPane";
import MailboxPane from "@/components/admin/MailboxPane";
import { StaffContextCard } from "@/components/admin/StaffContextCard";
import { NewChannelModal, NewGroupModal } from "@/components/inbox/StaffCreateModals";
import { resolveConvType, SIDEBAR_GROUPS } from "@/lib/conversationTheme";
import { Search, X, Hash, MessageSquare, Store, LifeBuoy, Users2, ShieldCheck, MessagesSquare, ArrowLeft, Plus, AtSign, Bell } from "lucide-react";
import { timeAgo } from "@/lib/constants";

function SectionHead({ icon: Icon, label, count, action }) {
  return (
    <div className="px-4 pt-3 pb-1 flex items-center gap-2" data-testid={`inbox-section-${label.toLowerCase()}`}>
      <Icon className="w-3.5 h-3.5 text-olive-bright" />
      <span className="font-mono text-[9px] uppercase tracking-widest text-muted-foreground">{label}</span>
      {count > 0 && <span className="ml-auto font-mono text-[9px] px-1.5 rounded-full bg-blaze text-black">{count}</span>}
      {action && <span className={count > 0 ? "" : "ml-auto"}>{action}</span>}
    </div>
  );
}

const Unread = ({ n }) => (n > 0 ? <span className="bg-blaze text-black text-[9px] font-bold px-1 shrink-0">{n}</span> : null);

export default function Inbox({ embedded = false }) {
  const { conversationId } = useParams();
  const navigate = useNavigate();
  const { search } = useLocation();
  const wantTeam = new URLSearchParams(search).get("view") === "team";
  const { user } = useAuth();
  const admin = isAdminRole(user?.role);
  const verified = !!user?.identity_verified;
  const { refresh: refreshUnread } = useInboxUnread();

  const [summary, setSummary] = useState(null);
  const [convs, setConvs] = useState([]);
  const [mailboxes, setMailboxes] = useState([]);
  const [channels, setChannels] = useState([]);
  const [dms, setDms] = useState([]);
  const [groups, setGroups] = useState([]);
  const [forumNotifs, setForumNotifs] = useState([]);
  const [newChannelOpen, setNewChannelOpen] = useState(false);
  const [newGroupOpen, setNewGroupOpen] = useState(false);
  const [sel, setSel] = useState(null); // {scope:'marketplace'|'mailbox'|'channel'|'dm', id, ...}
  const [query, setQuery] = useState("");
  const [staffResults, setStaffResults] = useState(null);
  // Narrow screens are MASTER-DETAIL, not stacked panes: list is default, tapping opens the thread.
  const [isMobile, setIsMobile] = useState(() => typeof window !== "undefined" && window.matchMedia("(max-width: 767px)").matches);
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 767px)");
    const on = () => setIsMobile(mq.matches);
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, []);

  const loadSummary = useCallback(async () => {
    try { const { data } = await api.get("/inbox/summary"); setSummary(data); } catch {}
  }, []);

  const loadMarketplace = useCallback(async () => {
    if (!verified) return;
    try { const { data } = await api.get("/conversations"); setConvs(data.conversations || []); } catch {}
  }, [verified]);

  const loadStaff = useCallback(async () => {
    if (!admin) return;
    try {
      const [c, d] = await Promise.all([api.get("/admin/team/channels"), api.get("/admin/team/dms")]);
      setChannels(c.data.channels || []);
      setDms(d.data.dms || []);
    } catch {}
    try { const { data } = await api.get("/admin/mailboxes"); setMailboxes(data.mailboxes || []); } catch {}
  }, [admin]);

  // Group chats are universal — every logged-in member can be in one, so this loads for everyone.
  const loadGroups = useCallback(async () => {
    try { const { data } = await api.get("/groups"); setGroups(data.groups || []); } catch {}
  }, []);

  // Forum notifications (@-mentions + watched-thread replies) — every logged-in member.
  const loadForum = useCallback(async () => {
    try { const { data } = await api.get("/forum/notifications"); setForumNotifs(data.notifications || []); } catch {}
  }, []);

  const refreshAll = useCallback(() => { loadSummary(); loadMarketplace(); loadStaff(); loadGroups(); loadForum(); refreshUnread(); }, [loadSummary, loadMarketplace, loadStaff, loadGroups, loadForum, refreshUnread]);

  useEffect(() => { refreshAll(); }, [refreshAll]);
  useEffect(() => {
    const t = setInterval(() => { loadSummary(); loadMarketplace(); loadGroups(); loadForum(); }, 6000);
    return () => clearInterval(t);
  }, [loadSummary, loadMarketplace, loadGroups, loadForum]);

  const openForumNotif = async (n) => {
    try { await api.post("/forum/notifications/read", { ids: [n.notif_id] }); } catch {}
    setForumNotifs((p) => p.map((x) => (x.notif_id === n.notif_id ? { ...x, read: true } : x)));
    refreshUnread(); loadSummary();
    try {
      const u = new URL(n.link);
      navigate(u.pathname + u.hash);
    } catch { if (n.thread_id) navigate(`/forum/thread/${n.thread_id}`); }
  };

  // Deep link: /inbox/:conversationId opens that marketplace thread. Keeps the selection in sync
  // with the URL + loaded list, but NEVER lets a background poll (which swaps the `convs` array
  // reference every 6s) rebuild or clear the currently-open thread.
  useEffect(() => {
    if (conversationId && convs.length) {
      const c = convs.find((x) => x.conversation_id === conversationId);
      if (c) {
        setSel((prev) => {
          // Selecting a Support mailbox / channel / staff DM does NOT push a URL id, so the
          // marketplace `conversationId` can linger in the URL while the admin reads one of those.
          // A 6s poll rebuilds `convs` and re-runs this effect — without this guard it would yank
          // the admin back to the stale marketplace thread. Never override a non-marketplace pane.
          if (prev && prev.scope !== "marketplace") return prev;
          return (prev && prev.scope === "marketplace" && prev.id === conversationId)
            ? prev
            : { scope: "marketplace", id: conversationId, conv: c };
        });
      }
    } else if (!conversationId && !isMobile && !admin) {
      // desktop only: default the right pane to the first thread when nothing is selected yet
      setSel((prev) => prev || (convs.length ? { scope: "marketplace", id: convs[0].conversation_id, conv: convs[0] } : null));
    }
    // eslint-disable-next-line
  }, [conversationId, convs, isMobile]);

  // Mobile "return to list" is driven by the URL ALONE: only a real navigation that drops the
  // conversation id (browser Back / the Back-to-inbox tap) falls back to the list. Keying this on
  // `conversationId` only — NOT on `convs` — is what stops the 6s poll (and each pane's activity
  // refresh) from closing a staff DM / channel / mailbox thread, none of which carry a URL id.
  useEffect(() => {
    if (!conversationId && isMobile) setSel(null);
    // eslint-disable-next-line
  }, [conversationId]);

  // Team-chat deep link (/inbox?view=team) — the admin messages icon lands here. Default the right
  // pane to the first staff channel (or a staff DM) instead of a seller thread, on desktop only.
  useEffect(() => {
    if (!wantTeam || !admin || conversationId || isMobile) return;
    setSel((prev) => {
      if (prev) return prev;
      if (channels.length) { const c = channels[0]; return { scope: "channel", id: c.channel_id, name: c.name, topic: c.topic }; }
      if (dms.length) { const d = dms[0]; return { scope: "dm", id: d.user_id, name: d.name || d.username, username: d.username }; }
      return prev;
    });
    // eslint-disable-next-line
  }, [wantTeam, admin, conversationId, isMobile, channels, dms]);

  const runSearch = async (e) => {
    e?.preventDefault?.();
    if (!query.trim()) { setStaffResults(null); return; }
    if (admin) {
      try { const { data } = await api.get("/admin/team/search", { params: { q: query.trim() } }); setStaffResults(data.results || []); }
      catch { setStaffResults([]); }
    }
  };

  const q = query.trim().toLowerCase();
  const filteredConvs = useMemo(() => !q ? convs : convs.filter((c) =>
    (c.other_user?.username || "").toLowerCase().includes(q) ||
    (c.listing_title || "").toLowerCase().includes(q) ||
    (c.last_message || "").toLowerCase().includes(q)), [convs, q]);
  const filteredChannels = useMemo(() => !q ? channels : channels.filter((c) => c.name.toLowerCase().includes(q) || (c.topic || "").toLowerCase().includes(q)), [channels, q]);
  const filteredDms = useMemo(() => !q ? dms : dms.filter((d) => (d.username || "").toLowerCase().includes(q) || (d.name || "").toLowerCase().includes(q)), [dms, q]);
  const filteredGroups = useMemo(() => !q ? groups : groups.filter((g) => (g.title || "").toLowerCase().includes(q) || (g.member_usernames || []).some((u) => u.toLowerCase().includes(q))), [groups, q]);
  const filteredMailboxes = useMemo(() => !q ? mailboxes : mailboxes.filter((m) => m.address.toLowerCase().includes(q)), [mailboxes, q]);

  const select = (s) => {
    setStaffResults(null);
    setSel(s);
    // Push (not replace) so browser Back is a natural "return to the list" gesture on mobile.
    if (s.scope === "marketplace") navigate(`/inbox/${s.id}`);
  };

  const backToList = () => {
    setSel(null);
    setStaffResults(null);
    navigate("/inbox");
  };

  const mobileDetail = isMobile && (!!sel || staffResults !== null || (!verified && !admin));

  // While a thread is open on a narrow viewport, flag the body so the global beta Feedback FAB
  // lifts ABOVE the composer (see App.css) instead of floating over the "Message #…" input.
  useEffect(() => {
    document.body.classList.toggle("inbox-thread-open", mobileDetail);
    return () => document.body.classList.remove("inbox-thread-open");
  }, [mobileDetail]);

  const rowCls = (active) => `w-full text-left flex items-center gap-2.5 px-4 py-2 transition-colors border-l-2 ${active ? "bg-olive/20 text-foreground border-olive-bright" : "text-secondary-foreground hover:bg-white/5 hover:text-foreground border-transparent"}`;

  return (
    <div className={embedded ? "flex-1 min-h-0 flex flex-col text-foreground" : "h-[100dvh] flex flex-col bg-obsidian text-foreground overflow-hidden"}>
      {!embedded && <Navbar />}
      <div className={`w-full max-w-[1320px] mx-auto flex-1 min-h-0 flex flex-col md:px-4 md:py-6 ${mobileDetail ? "px-0 py-0" : "px-4 py-6"}`}>
        <h1 className={`shrink-0 font-heading text-3xl font-bold uppercase tracking-tight mb-4 ${embedded ? "hidden" : (mobileDetail ? "hidden md:block" : "")}`} data-testid="inbox-title">Inbox</h1>
        <div className={`grid grid-cols-1 md:grid-cols-[300px_1fr] min-w-0 bg-surface md:border border-sniper flex-1 min-h-0 md:overflow-hidden ${mobileDetail ? "border-0" : "border overflow-hidden"}`} data-testid="inbox-shell">
          {/* left rail — full-width LIST view on mobile; hidden once a thread is open */}
          <div className={`border-r border-sniper flex-col min-h-0 min-w-0 h-full md:flex ${mobileDetail ? "hidden" : "flex"}`}>
            <form onSubmit={runSearch} className="p-2 border-b border-sniper flex gap-1">
              <input value={query} onChange={(e) => setQuery(e.target.value)} data-testid="inbox-search-input"
                placeholder="Search all messages…" className="flex-1 min-w-0 bg-input border border-sniper focus:border-olive outline-none px-2 py-1.5 text-xs text-foreground transition-colors" />
              <button type="submit" data-testid="inbox-search-btn" className="border border-sniper hover:border-olive text-muted-foreground hover:text-foreground px-2 transition-colors"><Search className="w-3.5 h-3.5" /></button>
            </form>
            <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden overscroll-contain pt-1 pb-24 md:pb-1" data-testid="inbox-rail">
              {(summary?.mentions || 0) > 0 && (
                <div className="px-4 pt-3" data-testid="inbox-mentions-indicator">
                  <div className="flex items-center gap-2 border border-olive-bright/40 bg-olive/10 px-2.5 py-1.5">
                    <AtSign className="w-3.5 h-3.5 text-olive-bright shrink-0" />
                    <span className="font-mono text-[10px] uppercase tracking-wide text-olive-bright">{summary.mentions} unread mention{summary.mentions === 1 ? "" : "s"}</span>
                  </div>
                </div>
              )}
              {/* FORUM — @-mentions + watched-thread replies (links out to the forum) */}
              <SectionHead icon={AtSign} label="Forum" count={summary?.sections?.forum || 0} />
              {forumNotifs.length === 0 ? (
                <p className="px-4 py-1 text-[10px] font-mono text-muted-foreground" data-testid="inbox-forum-empty">No forum activity yet.</p>
              ) : (
                forumNotifs.slice(0, 30).map((n) => (
                  <button key={n.notif_id} onClick={() => openForumNotif(n)} data-testid={`inbox-forum-notif-${n.notif_id}`}
                    className={`w-full text-left flex items-start gap-2.5 px-4 py-2 transition-colors border-l-2 ${n.read ? "text-secondary-foreground border-transparent hover:bg-white/5" : "text-foreground border-olive-bright bg-olive/10 hover:bg-olive/20"}`}>
                    <span className="mt-0.5 shrink-0">{n.kind === "mention" ? <AtSign className="w-3.5 h-3.5 text-olive-bright" /> : <Bell className="w-3.5 h-3.5 text-olive-bright" />}</span>
                    <span className="min-w-0 flex-1">
                      <span className="block font-mono text-[11px] leading-tight">
                        <span className="text-olive-bright">@{n.actor_username}</span>{" "}
                        <span className="text-muted-foreground">{n.kind === "mention" ? "mentioned you in" : "replied in"}</span>
                      </span>
                      <span className="block text-xs font-body truncate">{n.thread_title || "a thread"}</span>
                      {n.snippet && <span className="block text-[11px] text-muted-foreground truncate">{n.snippet}</span>}
                      <span className="block text-[10px] text-muted-foreground mt-0.5">{timeAgo(n.created_at)}</span>
                    </span>
                    {!n.read && <span className="mt-1 w-1.5 h-1.5 rounded-full bg-olive-bright shrink-0" />}
                  </button>
                ))
              )}

              {/* GROUPS — private, multi-person; available to EVERY member, pinned to the top */}
              <SectionHead icon={MessagesSquare} label="Groups" count={summary?.sections?.groups || 0}
                action={<button onClick={() => setNewGroupOpen(true)} data-testid="inbox-new-group-btn" title="New group chat" className="text-muted-foreground hover:text-olive-bright transition-colors"><Plus className="w-3.5 h-3.5" /></button>} />
              {filteredGroups.map((g) => {
                const active = sel?.scope === "group" && sel.id === g.group_id;
                return (
                  <button key={g.group_id} onClick={() => select({ scope: "group", id: g.group_id, name: g.title, topic: `${g.member_count} members` })}
                    data-testid={`inbox-group-${g.group_id}`} className={rowCls(active)}>
                    <MessagesSquare className="w-3.5 h-3.5 shrink-0" /><span className="font-mono text-xs flex-1 truncate">{g.title}</span><Unread n={g.unread} />
                  </button>
                );
              })}
              {filteredGroups.length === 0 && <p className="px-4 py-1 text-[10px] font-mono text-muted-foreground">No group chats yet.</p>}

              {/* MARKETPLACE */}
              <SectionHead icon={Store} label="Marketplace" count={summary?.sections?.marketplace || 0} />
              {!verified && (
                <p className="px-4 py-2 text-[11px] font-mono text-muted-foreground" data-testid="inbox-marketplace-locked">Verify your ID to use marketplace messaging.</p>
              )}
              {verified && filteredConvs.length === 0 && <p className="px-4 py-2 text-[11px] font-mono text-muted-foreground">No conversations.</p>}
              {verified && filteredConvs.map((c) => {
                const grp = SIDEBAR_GROUPS[resolveConvType(c.other_user)];
                const active = sel?.scope === "marketplace" && sel.id === c.conversation_id;
                return (
                  <button key={c.conversation_id} onClick={() => select({ scope: "marketplace", id: c.conversation_id, conv: c })}
                    data-testid={`conversation-${c.conversation_id}`} className={rowCls(active)} style={{ borderLeftColor: active ? undefined : grp.color }}>
                    <div className="w-9 h-9 shrink-0 border border-sniper bg-obsidian overflow-hidden">
                      {c.listing_photo ? <img src={c.listing_photo} alt="" className="w-full h-full object-cover" /> : <MessageSquare className="w-full h-full p-2 text-muted-foreground" />}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1 min-w-0"><span className="font-mono text-xs text-olive-bright truncate min-w-0">@{c.other_user.username}</span><span className="ml-auto shrink-0"><Unread n={c.unread} /></span></div>
                      <p className="text-[11px] text-muted-foreground truncate font-body">{c.listing_title}</p>
                    </div>
                  </button>
                );
              })}

              {admin && (
                <>
                  {/* SUPPORT */}
                  <SectionHead icon={LifeBuoy} label="Support" count={summary?.sections?.support || 0} />
                  {filteredMailboxes.map((mb) => {
                    const [local, domain] = mb.address.split("@");
                    const active = sel?.scope === "mailbox" && sel.id === mb.address;
                    return (
                      <button key={mb.address} onClick={() => select({ scope: "mailbox", id: mb.address, status: mb.status })}
                        data-testid={`mailbox-nav-${mb.address}`} className={rowCls(active)}>
                        <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: mb.status === "send_only" ? "#6B6D70" : "#7FA648" }} />
                        <span className="truncate font-mono text-xs flex-1"><span className="text-foreground">{local}</span><span className="text-muted-foreground">@{domain}</span></span>
                        <Unread n={mb.unread} />
                      </button>
                    );
                  })}
                  {filteredMailboxes.length === 0 && <p className="px-4 py-1 text-[10px] font-mono text-muted-foreground">No mailboxes.</p>}

                  {/* INTERNAL */}
                  <SectionHead icon={Hash} label="Internal" count={summary?.sections?.internal || 0}
                    action={<button onClick={() => setNewChannelOpen(true)} data-testid="inbox-new-channel-btn" title="New channel" className="text-muted-foreground hover:text-olive-bright transition-colors"><Plus className="w-3.5 h-3.5" /></button>} />
                  {filteredChannels.map((c) => {
                    const active = sel?.scope === "channel" && sel.id === c.channel_id;
                    return (
                      <button key={c.channel_id} onClick={() => select({ scope: "channel", id: c.channel_id, name: c.name, topic: c.topic })}
                        data-testid={`staff-channel-${c.channel_id}`} className={rowCls(active)}>
                        <Hash className="w-3.5 h-3.5 shrink-0" /><span className="font-mono text-xs flex-1 truncate">{c.name}</span><Unread n={c.unread} />
                      </button>
                    );
                  })}

                  {/* STAFF */}
                  <SectionHead icon={Users2} label="Staff" count={summary?.sections?.staff || 0} />
                  {filteredDms.map((d) => {
                    const active = sel?.scope === "dm" && sel.id === d.user_id;
                    const displayName = d.name || d.username;
                    return (
                      <button key={d.user_id} onClick={() => select({ scope: "dm", id: d.user_id, name: displayName, username: d.username })}
                        data-testid={`staff-dm-${d.user_id}`} className={rowCls(active)}>
                        <MessageSquare className="w-3.5 h-3.5 shrink-0" />
                        <span className="flex-1 min-w-0">
                          <span className="block font-heading text-xs uppercase tracking-tight truncate" data-testid={`staff-dm-name-${d.user_id}`}>{displayName}</span>
                          {d.name && <span className="block font-mono text-[10px] text-muted-foreground truncate">@{d.username}</span>}
                        </span>
                        <Unread n={d.unread} />
                      </button>
                    );
                  })}
                  {filteredDms.length === 0 && <p className="px-4 py-1 text-[10px] font-mono text-muted-foreground">No other staff yet.</p>}
                </>
              )}
            </div>
          </div>

          {/* content — full-screen THREAD view on mobile (fixed overlay above the navbar so the
              back bar is always tappable and nothing overlaps); normal right pane on desktop */}
          <div className={mobileDetail
            ? "fixed inset-0 z-[80] flex flex-col h-[100dvh] bg-obsidian"
            : "flex-col min-w-0 min-h-0 hidden md:flex md:h-full"}>
            {mobileDetail && (
              <button onClick={backToList} data-testid="inbox-mobile-back"
                className="flex items-center gap-2 px-3 py-3 border-b border-sniper bg-obsidian text-olive-bright font-mono uppercase text-xs shrink-0">
                <ArrowLeft className="w-4 h-4" /> Back to inbox
              </button>
            )}
            <div className="flex-1 min-h-0 flex flex-col">
            {staffResults !== null ? (
              <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden overscroll-contain" data-testid="inbox-search-results">
                <div className="px-4 py-3 border-b border-sniper flex items-center justify-between">
                  <span className="font-heading uppercase text-foreground flex items-center gap-2"><MessagesSquare className="w-4 h-4 text-olive-bright" /> Messages: “{query}” · {staffResults.length}</span>
                  <button onClick={() => { setStaffResults(null); }} data-testid="inbox-search-clear" className="text-muted-foreground hover:text-foreground"><X className="w-4 h-4" /></button>
                </div>
                {staffResults.length === 0 && <p className="p-6 text-sm font-mono text-muted-foreground">No message matches. Filtered rows on the left still match “{query}”.</p>}
                {staffResults.map((m) => (
                  <div key={m.message_id} className="px-4 py-2 border-b border-sniper">
                    <p className="font-mono text-[10px] uppercase text-muted-foreground">{m.channel_name ? `#${m.channel_name}` : "DM"} · @{m.sender_username} · {timeAgo(m.created_at)}</p>
                    <p className="text-sm font-body text-foreground mt-0.5">{m.text}</p>
                    {(m.context_cards || []).map((c) => <StaffContextCard key={`${c.type}-${c.id}`} card={c} />)}
                  </div>
                ))}
              </div>
            ) : !verified && !admin ? (
              <div className="flex-1 flex items-center justify-center p-8">
                <div className="text-center max-w-[420px]" data-testid="messaging-verify-gate">
                  <ShieldCheck className="w-10 h-10 mx-auto text-olive-bright" strokeWidth={1.5} />
                  <h2 className="mt-4 font-heading text-2xl uppercase tracking-tight">Verify your ID to use messaging</h2>
                  <p className="mt-3 text-sm font-body text-secondary-foreground">A quick, two-minute Stripe ID check unlocks reading and sending on every thread.</p>
                  <button onClick={() => navigate("/verify")} data-testid="messaging-verify-cta" className="mt-6 inline-flex items-center gap-2 bg-olive hover:bg-olive-bright text-white font-heading font-bold uppercase px-6 py-2.5 transition-colors">
                    <ShieldCheck className="w-4 h-4" /> Verify my ID
                  </button>
                </div>
              </div>
            ) : sel?.scope === "marketplace" ? (
              <MarketplaceThread conversation={sel.conv} onHidden={refreshAll} onActivity={refreshAll} />
            ) : sel?.scope === "mailbox" ? (
              <MailboxPane address={sel.id} status={sel.status} onCountsChanged={refreshAll} />
            ) : sel?.scope === "channel" || sel?.scope === "dm" || sel?.scope === "group" ? (
              <StaffChatPane view={{ kind: sel.scope, id: sel.id, name: sel.name, username: sel.username, topic: sel.topic }} onActivity={refreshAll} onExitGroup={() => { setSel(null); loadGroups(); }} />
            ) : (
              <div className="flex-1 flex items-center justify-center text-muted-foreground font-mono uppercase text-sm">Select a conversation</div>
            )}
            </div>
          </div>
        </div>
        <p className={`shrink-0 mt-3 mr-32 mb-6 md:mr-0 md:mb-0 text-[11px] font-body text-muted-foreground ${mobileDetail ? "hidden md:block" : ""}`} data-testid="messages-retention-note">
          Messages are retained for up to 7 years and may be reviewed by staff to resolve a dispute or report.{" "}
          <a href="/privacy" className="text-olive-bright hover:text-foreground underline">Learn more</a>.
        </p>
      </div>

      {newChannelOpen && (
        <NewChannelModal
          onClose={() => setNewChannelOpen(false)}
          onCreated={(ch) => { setNewChannelOpen(false); loadStaff(); setSel({ scope: "channel", id: ch.channel_id, name: ch.name, topic: ch.topic }); }}
        />
      )}
      {newGroupOpen && (
        <NewGroupModal
          currentUserId={user?.user_id}
          onClose={() => setNewGroupOpen(false)}
          onCreated={(g) => { setNewGroupOpen(false); loadGroups(); setSel({ scope: "group", id: g.group_id, name: g.title, topic: `${g.member_count} members` }); }}
        />
      )}
    </div>
  );
}
